import { DeleteDocument } from "./DeleteDocument";
import { DocsPageDetails } from "./DocsPageDetails";
import { uploadDocImage } from "./docs-maintenance-api";
import type { OperationsNavigate } from "./navigation";
import { useQueryClient } from "@tanstack/react-query";
import { Check, CloudOff, Download, Eye, Loader2, Save } from "lucide-react";
import { useCallback, useState, useSyncExternalStore } from "react";
import { documentMarkdown, markdownBlocks } from "../knowledge/document-markdown";
import type { OpsDocLinkTarget, OpsDocPage } from "../../shared/operations-docs-schemas";
import { ApiClientError } from "../api/client-core";
import { Button } from "../components/ui/button";
import { DocumentTitle } from "../knowledge/DocumentTitle";
import { DocumentEditor } from "../knowledge/DocumentEditor";
import { DocumentSession } from "../knowledge/document-session";
import { DocsPageLinksDialog } from "./DocsPageLinksDialog";
import { DocsConflictReview } from "./DocsConflictReview";
import { updateOpsDocsPage } from "./docs-api";

function downloadDraft(session: DocumentSession) {
  const { title, markdown } = session.snapshot().draft;
  const url = URL.createObjectURL(
    new Blob([`# ${title}\n\n${markdown}`], { type: "text/markdown;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${title.replace(/[^\p{L}\p{N} _-]/gu, "").slice(0, 100) || "document"}.md`;
  anchor.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

export function SaveStatus({ session }: { session: DocumentSession }) {
  const { state, message } = useSyncExternalStore(session.subscribe, session.snapshot);
  const Icon =
    state === "saved"
      ? Check
      : state === "saving"
        ? Loader2
        : state === "unsaved"
          ? Save
          : CloudOff;
  const label = {
    saved: "All changes saved",
    saving: "Saving…",
    unsaved: "Unsaved changes",
    error: "Save failed",
    conflict: "Newer version available",
  }[state];
  return (
    <span
      className="flex min-w-36 items-center gap-1.5 text-xs text-muted-foreground"
      role="status"
      title={message ?? undefined}
    >
      <Icon className={`size-3.5 ${state === "saving" ? "motion-safe:animate-spin" : ""}`} />
      {label}
    </span>
  );
}

export function RecoveryNotice({ session }: { session: DocumentSession }) {
  const { state, message } = useSyncExternalStore(session.subscribe, session.snapshot);
  if (!message) return null;
  return (
    <div
      role="alert"
      className="my-4 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm"
    >
      <p>
        {state === "conflict"
          ? "Someone saved a newer version. Your draft is still here. Download it before reopening the latest version; your changes have not overwritten theirs."
          : message}
      </p>
      <div className="mt-3 flex gap-2">
        {state === "error" ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void session.flush();
            }}
          >
            Retry save
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            downloadDraft(session);
          }}
        >
          <Download />
          Download draft
        </Button>
      </div>
    </div>
  );
}

type Props = {
  navigate: OperationsNavigate;
  page: OpsDocPage;
  linkTargets: OpsDocLinkTarget[];
  onPreview: () => void;
};
export function DocsDocumentCanvas({ page, linkTargets, onPreview, navigate }: Props) {
  const queryClient = useQueryClient();
  const [session] = useState(
    () =>
      new DocumentSession({
        draft: {
          title: page.title,
          markdown: documentMarkdown(page.blocks),
          links: page.links.map(({ entityType, entityId }) => ({ entityType, entityId })),
        },
        revision: page.revision,
        isConflict: (error) => error instanceof ApiClientError && error.status === 409,
        save: async (draft, revision) => {
          const response = await updateOpsDocsPage(page.id, {
            expectedRevision: revision,
            title: draft.title,
            blocks: markdownBlocks(draft.markdown),
            links: draft.links,
          });
          queryClient.setQueryData(["ops", "docs", "page", page.id], response);
          void queryClient.invalidateQueries({ queryKey: ["ops", "docs", "bootstrap"] });
          return response.page.revision;
        },
      }),
  );
  return (
    <CanvasContent
      navigate={navigate}
      session={session}
      page={page}
      linkTargets={linkTargets}
      onPreview={onPreview}
    />
  );
}

function CanvasContent({
  session,
  navigate,
  page,
  linkTargets,
  onPreview,
}: Props & { session: DocumentSession }) {
  const mount = useCallback((node: HTMLDivElement | null) => session.attach(node), [session]);
  const { draft, generation, state } = useSyncExternalStore(session.subscribe, session.snapshot);
  return (
    <div ref={mount} className="min-w-0">
      <CanvasToolbar
        page={page}
        navigate={navigate}
        session={session}
        linkTargets={linkTargets}
        onPreview={onPreview}
      />
      <article className="mx-auto w-full max-w-[860px] px-5 pt-7 pb-16 sm:px-12 sm:pt-9">
        <DocsPageDetails page={page} canEdit session={session} navigate={navigate} />
        <DocumentTitle
          value={draft.title}
          onChange={(title) => {
            session.change({ title });
          }}
          onSave={() => {
            void session.flush();
          }}
        />
        <RecoveryNotice session={session} />
        {state === "conflict" ? <DocsConflictReview pageId={page.id} session={session} /> : null}
        <DocumentEditor
          key={generation}
          pageId={page.id}
          uploadImage={(file, signal) => uploadDocImage(page.id, file, signal)}
          markdown={draft.markdown}
          onChange={(markdown) => {
            session.change({ markdown });
          }}
          onSave={() => {
            void session.flush();
          }}
        />
      </article>
    </div>
  );
}

function CanvasToolbar({
  page,
  navigate,
  session,
  linkTargets,
  onPreview,
}: Props & { session: DocumentSession }) {
  const { draft, state } = useSyncExternalStore(session.subscribe, session.snapshot);
  const selectedLinks = draft.links.map((link) => `${link.entityType}:${link.entityId}`);
  const preview = async () => {
    await session.flush();
    if (session.canLeave()) onPreview();
  };
  return (
    <div className="flex min-h-12 flex-wrap items-center justify-between gap-2 border-b px-4 py-2">
      <SaveStatus session={session} />
      <div className="flex items-center gap-1">
        {page.accessLevel === "manage" ? (
          <DeleteDocument
            source="internal"
            id={page.id}
            revision={page.revision}
            title={page.title}
            disabled={state !== "saved"}
            onDeleted={() => {
              navigate("/docs");
            }}
          />
        ) : null}
        <DocsPageLinksDialog
          linkTargets={linkTargets}
          selectedLinks={selectedLinks}
          setSelectedLinks={(update) => {
            const next = typeof update === "function" ? update(selectedLinks) : update;
            session.change({
              links: linkTargets
                .filter((target) => next.includes(`${target.entityType}:${target.entityId}`))
                .map(({ entityType, entityId }) => ({ entityType, entityId })),
            });
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            downloadDraft(session);
          }}
          aria-label="Download Markdown"
        >
          <Download />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={state === "saving" || state === "conflict"}
          onClick={() => {
            void preview();
          }}
        >
          <Eye />
          Preview & share
        </Button>
      </div>
    </div>
  );
}
